import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { OperationsTeam } from "../../types/operationsTeam";
import {
  ORDER_STATUS_VALUES,
  type Order,
  type OrderSearchFilters,
  calculateOrderExpenseTotal,
  getReportSelectionBlockReasonKey,
  formatOrderLocations,
} from "../../types/order";
import type { PaginationParam, PaginationResponse } from "../../types/paginationResponse";
import type { StandbyOrder } from "../../types/standbyOrder";
import { formatLicensePlateInput } from "../../utils/formatLicensePlate";
import { formatRupiah } from "../../utils/formatRupiah";
import { mergeOrderListRows } from "../../utils/mergeOrderListRows";
import { getOrderStatusAccentColor } from "../../utils/orderStatusAccent";
import DetailSectionCard from "../common/DetailSectionCard";
import DisabledButtonTooltip from "../common/DisabledButtonTooltip";
import OrderStatusChip from "../common/OrderStatusChip";
import DataTable from "../common/Table";

type TranslationKeys = keyof typeof translations.en;

interface StatusOption {
  value: string;
  label: string;
}

interface OrderTableProps {
  orders: Order[];
  standbyOrders?: StandbyOrder[];
  teams: OperationsTeam[];
  filters: OrderSearchFilters;
  loading?: boolean;
  pagination?: PaginationResponse | null;
  onFiltersChange: (filters: OrderSearchFilters) => void;
  onSearch: (filters: OrderSearchFilters, pagination: PaginationParam) => Promise<void>;
  onPaginationChange?: (pagination: PaginationParam) => void;
  reportSelectMode?: boolean;
  selectedOrderIds?: string[];
  selectedTeamOperationsId?: string;
  onToggleOrderSelection?: (order: Order) => void;
  onToggleStandbySelection?: (order: StandbyOrder) => void;
  canAddOrder?: boolean;
  onAddOrder?: () => void;
}

const filterButtonSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  borderRadius: 1.5,
};

const OrderTable: React.FC<OrderTableProps> = ({
  orders,
  standbyOrders = [],
  teams,
  filters,
  loading,
  pagination,
  onFiltersChange,
  onSearch,
  onPaginationChange,
  reportSelectMode = false,
  selectedOrderIds = [],
  selectedTeamOperationsId,
  onToggleOrderSelection,
  onToggleStandbySelection,
  canAddOrder = false,
  onAddOrder,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSearch = async () => {
    await onSearch(filters, { page: 1, pageSize: pagination?.pageSize || 10 });
  };

  const handleClearFilters = async () => {
    const cleared: OrderSearchFilters = { status: "", teamOperationsId: "" };
    onFiltersChange(cleared);
    await onSearch(cleared, { page: 1, pageSize: pagination?.pageSize || 10 });
  };

  const hasActiveFilters = Boolean(filters.status || filters.teamOperationsId);

  const renderReportSelectCheckbox = (
    testId: string,
    item: Order | StandbyOrder,
    isSelected: boolean,
    onToggle: (() => void) | undefined,
  ) => {
    const blockReasonKey = getReportSelectionBlockReasonKey(item, selectedTeamOperationsId);
    const disabled = Boolean(blockReasonKey);
    const disabledReason = blockReasonKey ? t(blockReasonKey as TranslationKeys) : "";

    return (
      <DisabledButtonTooltip title={disabledReason} disabled={disabled}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            cursor: disabled ? "not-allowed" : "pointer",
            bgcolor: disabled ? "action.disabledBackground" : "transparent",
            "& .MuiCheckbox-root": {
              color: disabled ? "text.disabled" : undefined,
            },
            "& .MuiSvgIcon-root": {
              opacity: disabled ? 0.45 : 1,
              color: disabled ? "text.disabled" : undefined,
            },
          }}
        >
          <Checkbox
            size="small"
            checked={isSelected}
            disabled={disabled}
            disableRipple={disabled}
            onClick={(event) => event.stopPropagation()}
            onChange={() => onToggle?.()}
            sx={{
              "&.Mui-disabled": {
                color: "text.disabled",
              },
            }}
            slotProps={{
              input: {
                "data-testid": testId,
                "aria-label": disabled ? disabledReason : undefined,
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
          />
        </Box>
      </DisabledButtonTooltip>
    );
  };

  const statusOptions: StatusOption[] = ORDER_STATUS_VALUES.map((status) => ({
    value: status,
    label: t(status as TranslationKeys),
  }));

  const teamOptions = teams.map((team) => ({
    value: team._id,
    label: team.name,
  }));

  const rows = mergeOrderListRows(orders, standbyOrders);

  const columns = [
    ...(reportSelectMode
      ? [{ id: "select", label: "", sortable: false, align: "center" as const }]
      : []),
    { id: "readableId", label: "ID", sortable: false },
    { id: "status", label: "Status", sortable: false },
    { id: "departureTimestamp", label: "Departure", sortable: false },
    { id: "licensePlate", label: "License Plate", sortable: false },
    { id: "driver", label: "Driver", sortable: false },
    { id: "customer", label: "Customer", sortable: false },
    { id: "externalOrderId", label: "No. LT/POD", sortable: false },
    { id: "load", label: "orders.load", sortable: false },
    { id: "unload", label: "orders.unload", sortable: false },
    { id: "expenseTotal", label: "orders.expenseTotal", sortable: false, align: "right" as const },
    { id: "team", label: "Team", sortable: false },
  ];

  return (
    <>
      <DetailSectionCard
        title={t("orders.filtersSection" as TranslationKeys)}
        icon={<FilterListOutlinedIcon fontSize="small" />}
        iconColor="#64748B"
        sx={{ mb: 2 }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
            },
            gap: 2,
          }}
        >
          <FormControl fullWidth size="small">
            <Autocomplete
              value={statusOptions.find((option) => option.value === filters.status) || null}
              onChange={(_, newValue) => {
                onFiltersChange({
                  ...filters,
                  status: newValue?.value || "",
                });
              }}
              options={[{ value: "", label: t("All" as TranslationKeys) }, ...statusOptions]}
              getOptionLabel={(option) =>
                option.value === "" ? t("All" as TranslationKeys) : option.label
              }
              renderOption={(props, option) => (
                <li {...props} key={option.value || "all"}>
                  {option.value ? (
                    <OrderStatusChip status={option.value} />
                  ) : (
                    t("All" as TranslationKeys)
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Status" as TranslationKeys)}
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": TestId.ORDER.STATUS_FILTER,
                  }}
                />
              )}
              size="small"
            />
          </FormControl>
          <FormControl fullWidth size="small">
            <Autocomplete
              value={
                teamOptions.find((option) => option.value === filters.teamOperationsId) || null
              }
              onChange={(_, newValue) => {
                onFiltersChange({
                  ...filters,
                  teamOperationsId: newValue?.value || "",
                });
              }}
              options={[{ value: "", label: "All" }, ...teamOptions]}
              getOptionLabel={(option) =>
                option.value === "" ? t("All" as TranslationKeys) : option.label
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Team" as TranslationKeys)}
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": TestId.ORDER.TEAM_FILTER,
                  }}
                />
              )}
              size="small"
            />
          </FormControl>
        </Box>
        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" size="small" onClick={handleSearch} sx={filterButtonSx}>
            {t("Search" as TranslationKeys)}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              sx={filterButtonSx}
            >
              {t("orders.clearFilters" as TranslationKeys)}
            </Button>
          )}
        </Box>
      </DetailSectionCard>

      <DataTable
        data={rows}
        columns={columns.map((column) => ({
          ...column,
          label: column.label ? t(column.label as TranslationKeys) : "",
        }))}
        loading={loading}
        showSearch={false}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        testId={TestId.ORDER.TABLE}
        renderRow={(row) => {
          if (row.kind === "standby") {
            const order = row.order;
            const isSelected = selectedOrderIds.includes(order._id);
            const accent = getOrderStatusAccentColor(order.status);
            const expenseTotal = (order.expenses ?? []).reduce(
              (sum, line) => sum + (line.amount ?? 0),
              0,
            );

            return (
              <TableRow
                key={order._id}
                hover={!reportSelectMode}
                selected={reportSelectMode && isSelected}
                data-testid={TestId.STANDBY.ROW}
                sx={{
                  cursor: reportSelectMode ? "default" : "pointer",
                  borderLeft: 4,
                  borderLeftColor: accent,
                  "&.Mui-selected": {
                    bgcolor: "action.selected",
                  },
                  "&.Mui-selected:hover": {
                    bgcolor: "action.hover",
                  },
                }}
                onClick={() => {
                  if (!reportSelectMode) {
                    navigate(`/orders/standby/${order.readableId}`);
                  }
                }}
              >
                {reportSelectMode && (
                  <TableCell padding="checkbox" sx={{ width: 42, px: 0.5 }}>
                    {renderReportSelectCheckbox(
                      TestId.STANDBY.ROW_CHECKBOX(order._id),
                      order,
                      isSelected,
                      onToggleStandbySelection ? () => onToggleStandbySelection(order) : undefined,
                    )}
                  </TableCell>
                )}
                <TableCell
                  sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                  data-testid={TestId.STANDBY.ROW_READABLE_ID(order.readableId)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {order.readableId}
                    <Chip
                      size="small"
                      label={t("orders.kindStandby" as TranslationKeys)}
                      sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 600 }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <OrderStatusChip status={order.status} />
                </TableCell>
                <TableCell>{order.date || "—"}</TableCell>
                <TableCell>
                  {order.truck?.licensePlate
                    ? formatLicensePlateInput(order.truck.licensePlate)
                    : "—"}
                </TableCell>
                <TableCell>{order.driver?.name || "—"}</TableCell>
                <TableCell>—</TableCell>
                <TableCell>—</TableCell>
                <TableCell>{order.location?.name || "—"}</TableCell>
                <TableCell>—</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatRupiah(expenseTotal)}
                </TableCell>
                <TableCell>{order.teamOperations?.name || "—"}</TableCell>
              </TableRow>
            );
          }

          const order = row.order;
          const isSelected = selectedOrderIds.includes(order._id);
          const accent = getOrderStatusAccentColor(order.status);

          return (
            <TableRow
              key={order._id}
              hover={!reportSelectMode}
              selected={reportSelectMode && isSelected}
              data-testid={TestId.ORDER.ROW}
              sx={{
                cursor: reportSelectMode ? "default" : "pointer",
                borderLeft: 4,
                borderLeftColor: accent,
                "&.Mui-selected": {
                  bgcolor: "action.selected",
                },
                "&.Mui-selected:hover": {
                  bgcolor: "action.hover",
                },
              }}
              onClick={() => {
                if (!reportSelectMode) {
                  navigate(`/orders/${order.readableId}`);
                }
              }}
            >
              {reportSelectMode && (
                <TableCell padding="checkbox" sx={{ width: 42, px: 0.5 }}>
                  {renderReportSelectCheckbox(
                    TestId.ORDER.ROW_CHECKBOX(order._id),
                    order,
                    isSelected,
                    onToggleOrderSelection ? () => onToggleOrderSelection(order) : undefined,
                  )}
                </TableCell>
              )}
              <TableCell
                sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                data-testid={TestId.ORDER.ROW_READABLE_ID(order.readableId)}
              >
                {order.readableId}
              </TableCell>
              <TableCell>
                <OrderStatusChip status={order.status} />
              </TableCell>
              <TableCell>{order.departureTimestamp || "—"}</TableCell>
              <TableCell>
                {order.truck?.licensePlate
                  ? formatLicensePlateInput(order.truck.licensePlate)
                  : "—"}
              </TableCell>
              <TableCell>{order.driver?.name || "—"}</TableCell>
              <TableCell>{order.customer?.name || "—"}</TableCell>
              <TableCell>{order.externalOrderId || "—"}</TableCell>
              <TableCell>{formatOrderLocations(order.load)}</TableCell>
              <TableCell>{formatOrderLocations(order.unload)}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupiah(calculateOrderExpenseTotal(order))}
              </TableCell>
              <TableCell>{order.teamOperations?.name || "—"}</TableCell>
            </TableRow>
          );
        }}
      />

      {rows.length === 0 && !loading && (
        <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: canAddOrder ? 2 : 0 }}>
            {t("No orders found" as TranslationKeys)}
          </Typography>
          {canAddOrder && onAddOrder && (
            <Button variant="contained" onClick={onAddOrder} sx={filterButtonSx}>
              {t("orders.emptyAddCta" as TranslationKeys)}
            </Button>
          )}
        </Box>
      )}
    </>
  );
};

export default OrderTable;
